<template>
  <v-flex>
    <v-divider class="mt-3 mb-3" />
    <div
      v-if="globalFormInvalid"
      class="v-message text-xs-center mt-3 mb-0"
    >
      <div class="v-messages__message">
        {{ $t('errors.form_not_valid') }}
      </div>
    </div>
    <v-layout
      :wrap="$vuetify.breakpoint.smAndDown"
      :justify-center="$vuetify.breakpoint.smAndDown"
      align-center
    >
      <v-spacer v-if="$vuetify.breakpoint.mdAndUp" />
      <v-flex shrink>
        <ScrollBottomButton v-if="scrollButton" />
        <FloatingSaveButton
          v-if="isDraftSaveVisible"
          :sheet-type="sheetType"
          :family-id="familyId"
          @clearFlags="clearValidatorFlags"
        />
        <v-btn
          v-if="false"
          color="sz_violet"
          class="mr-0 text-none"
          flat
          :to="{ name: 'family-card', params: { id: $route.params.familyId } }"
        >
          <v-icon>chevron_left</v-icon>
          {{ $t('sheet.previous_button') }}
        </v-btn>
      </v-flex>
      <v-flex shrink>
        <v-btn
          :loading="loader"
          color="sz_violet"
          class="mr-0 text-xs-center"
          :disabled="loader"
          @click="validateForm"
        >
          {{ saveButtonTitle }}
        </v-btn>
      </v-flex>
    </v-layout>
  </v-flex>
</template>

<script>
import FloatingSaveButton from '@/components/widgets/sheet/FloatingSaveButton';
import {
    cashReceiptsMemberSchema,
    chosenSourceDetailsSchema,
    disabledMemberSchema,
    illMemberSchema,
    joblessMemberSchema,
    noticePeriodMemberSchema
} from '@/schemas';
import fieldRemover from '@/utils/fieldsRemover';
import cloneDeep from 'lodash.clonedeep';

import ScrollBottomButton from '@/components/widgets/ScrollBottomButton';
import credits from '@/constants/sheet/credits';
import statusesId from '@/constants/statusesId';

import endButtonsCondition from '@/mixins/endButtonsCondition';

import scrollToTopErrorMessage from '@/mixins/scrollToTopErrorMessage';

import formatDecimalVariables from '@/mixins/formatDecimalVariables';
import isEmpty from 'lodash.isempty';
import has from 'lodash.has';

export default {
    name: 'SheetEndButtons',
    components: { ScrollBottomButton, FloatingSaveButton },
    mixins: [endButtonsCondition, scrollToTopErrorMessage, formatDecimalVariables],
    inject: ['$validator'],
    props: {
        nextSectionName: {
            required: true
        },
        to: {
            required: true
        },
        sheetType: {
            default: null
        },
        familyId: {
            default: null
        },
        readOnly: {
            default: false,
            type: Boolean
        },
        canDoAuditInArea: {
            default: false,
            type: Boolean
        },
        nationalCorrectingPermission: {
            default: false,
            type: Boolean
        },
        canDoAuditOnNational: {
            default: false,
            type: Boolean
        },
        scrollButton: {
            default: false
        }
    },
    data () {
        return {
            statusesId,
            credits,
            loader: false,
            globalFormInvalid: false
        };
    },
    computed: {
        publicationRevokedToNational () {
            return this.isSheetPublicationRevoked && this.nationalCorrectingPermission;
        },
        canDoAudit () {
            return this.canDoAuditInArea || this.canDoAuditOnNational;
        },
        isDraftSaveVisible () {
            if (this.readOnly === true) {
                return false;
            }
            return this.$store.getters['sheet/getIsSaveDraftVisible'];
        },
        validProps () {
            return Object.keys(this.fields).map((key) => this.fields[key].valid);
        },
        saveButtonTitle () {
            if (this.publicationRevokedCondition) {
                return this.$t('sheet.read_only_next_button') + ' ' + this.nextSectionName;
            }
            if (
                this.sheetType === 'decision' &&
        this.$store.getters['sheet/getDecisionFields'].isTurnOn === 'no'
            ) {
                return this.$t('sheet.previous_button');
            }
            if (this.canDoAudit) {
                return this.$t('sheet.next_button') + ' ' + this.nextSectionName;
            } else {
                return this.$t('sheet.next_button') + ' ' + this.nextSectionName;
            }
        },
        family () {
            return this.$store.getters['family/getFamily'];
        },
        isCurrentEdition () {
            return this.$store.getters['family/isCurrentEdition'];
        },
        publicationRevokedCondition () {
            return (
                (this.readOnly === true && this.publicationRevokedToNational !== true) ||
        (!this.isCurrentEdition && this.publicationRevokedToNational)
            );
        }
    },
    watch: {
        validProps: {
            handler (val) {
                if (!val.includes(false)) {
                    this.globalFormInvalid = false;
                }
            }
        }
    },
    methods: {
        sendSheetCorrections (data) {
            const _data = {
                familyId: this.familyId,
                sheetType: this.sheetType,
                data
            };
            return this.$store.dispatch('sheet/sendSheetCorrectionsArea', _data);
        },
        sendSheetCorrectionsNational (data) {
            const _data = {
                familyId: this.familyId,
                sheetType: this.sheetType,
                data
            };
            return this.$store.dispatch('sheet/sendSheetCorrectionsNational', _data);
        },
        sendSheetCorrectionsVolunteer (data) {
            const _data = {
                familyId: this.familyId,
                sheetType: this.sheetType,
                data
            };
            return this.$store.dispatch('sheet/sendSheetCorrectionsVolunteer', _data);
        },
        goToNextSection () {
            if (this.to === 'family-card') {
                this.$router.push({ name: this.to, params: { id: this.familyId } });
            } else {
                this.$router.push({ name: this.to });
            }
        },
        setButtonIndicatorProps (val) {
            this.loader = val;
        },
        validateForm () {
            if (this.publicationRevokedCondition) {
                if (this.to === 'family-card') {
                    this.$router.push({ name: this.to, params: { id: this.familyId } });
                    return;
                } else {
                    this.$router.push({ name: this.to });
                    return;
                }
            }
            this.setButtonIndicatorProps(true);
            this.globalFormInvalid = false;
            this.$validator.validateAll().then((valid) => {
                const isFormValid = valid && this.$validator?.errors?.items?.length < 1;
                this.globalFormInvalid = !isFormValid;
                if (isFormValid === true) {
                    let sheetJson = null;
                    if (this.sheetType === 'meeting') {
                        sheetJson = cloneDeep(this.$store.getters['sheet/getMeetingFields']);
                        fieldRemover(sheetJson);
                        fieldRemover(
                            sheetJson,
                            'previousEditions',
                            this.$store.getters['sheet/getPreviousEditionsField']('helpReceived') === 'no',
                            'helpAmount howDoYouRemember whatHasChanged hasImpactChange impactOnLife attemptToGetOutOfSituation attemptToGetOutOfSituationReason'
                        );
                        fieldRemover(
                            sheetJson,
                            'seniorsPack',
                            this.$store.getters['sheet/getJoblessField']('aboutSixty') === 'no' ||
                this.$store.getters['sheet/getJoblessField']('aboutSixty') === null
                        );
                        fieldRemover(
                            sheetJson,
                            'lawyersPack',
                            this.$store.getters['sheet/getCreditsField']('hasLawProblems') === 'no'
                        );

                        fieldRemover(
                            sheetJson,
                            'doctorsPack',
                            this.$store.getters['sheet/getExpensesForMedicineField'](
                                'difficultAccessToMedicine'
                            ) === 'no'
                        );
                        fieldRemover(
                            sheetJson,
                            'doctorsPack',
                            this.$store.getters['sheet/getExpensesForMedicineField'](
                                'difficultAccessToMedicalAssistance'
                            ) === 'no'
                        );
                        fieldRemover(
                            sheetJson,
                            'medicsPack',
                            this.$store.getters['sheet/getExpensesForMedicineField'](
                                'difficultAccessToMedicine'
                            ) === 'no'
                        );
                        fieldRemover(
                            sheetJson,
                            'medicsPack',
                            this.$store.getters['sheet/getExpensesForMedicineField'](
                                'difficultAccessToMedicalAssistance'
                            ) !== 'no'
                        );

                        fieldRemover(
                            sheetJson,
                            'jobless',
                            this.$store.getters['sheet/getJoblessField']('isReadyForWork') === 'no',
                            'members selectedMembers'
                        );
                        fieldRemover(
                            sheetJson,
                            'cashReceipts',
                            this.$store.getters['sheet/getCashReceiptsField']('isMembersWork') === 'no',
                            'activeMembers members'
                        );
                        fieldRemover(
                            sheetJson,
                            'cashReceipts',
                            this.$store.getters['sheet/getCashReceiptsField']('otherHelp') === 'no',
                            'otherHelpExplain'
                        );
                        fieldRemover(
                            sheetJson,
                            'cashReceipts',
                            this.$store.getters['sheet/getCashReceiptsField']('noticePeriod') === 'no',
                            'noticePeriodMembers'
                        );
                        fieldRemover(
                            sheetJson,
                            'cashReceipts',
                            this.$store.getters['sheet/getCashReceiptsField']('chosenSources').includes(103) ===
                false,
                            'chosenSourceOtherDescription'
                        );
                        fieldRemover(
                            sheetJson,
                            'credits',
                            this.$store.getters['sheet/getCreditsField']('hasCredits') === 'no',
                            'creditDetails totalAmount creditReason regularCreditRepair'
                        );
                        fieldRemover(
                            sheetJson,
                            'credits',
                            this.$store.getters['sheet/getCreditsField']('hasArrears') === 'no',
                            'arrearsAmount typeOfArrears'
                        );
                        fieldRemover(
                            sheetJson,
                            'credits',
                            this.$store.getters['sheet/getCreditsField']('hasInformalLiability') === 'no',
                            'informalLiabilityDescription informalLiabilityReason informalLiabilityOther informalLiabilityDetails informalLiabilityPending informalLiabilityTotalAmount'
                        );
                        fieldRemover(
                            sheetJson,
                            'credits',
                            this.$store.getters['sheet/getCreditsField']('informalLiabilityReason') !==
                this.credits.OTHER_INFORMAL_LIABILITY_REASON,
                            'informalLiabilityReasonOther'
                        );
                        fieldRemover(
                            sheetJson,
                            'expensesForMedicine',
                            this.$store.getters['sheet/getExpensesForMedicineField']('hasIllMembers') === 'no' &&
                this.$store.getters['sheet/getExpensesForMedicineField']('hasDisabledMembers') ===
                  'no',
                            'totalMedicineAmount members'
                        );
                        fieldRemover(
                            sheetJson,
                            'expensesForMedicine',
                            this.$store.getters['sheet/getExpensesForMedicineField']('coveredCosts') === 'yes',
                            'coveredCostsExplain'
                        );
                        fieldRemover(
                            sheetJson,
                            'expensesForMedicine',
                            this.$store.getters['sheet/getExpensesForMedicineField'](
                                'difficultAccessToMedicine'
                            ) !== 'yes',
                            'difficultAccessToMedicineReason'
                        );

                        fieldRemover(
                            sheetJson,
                            'maintenanceExpenses',
                            this.$store.getters['sheet/getMaintenanceExpensesField']('heating').includes(355) ===
                false,
                            'details'
                        );
                        fieldRemover(
                            sheetJson,
                            'maintenanceExpenses',
                            this.$store.getters['sheet/getMaintenanceExpensesField']('heating').includes(359) ===
                false,
                            'otherHeating'
                        );
                        fieldRemover(
                            sheetJson,
                            'maintenanceExpenses',
                            this.$store.getters['sheet/getMaintenanceExpensesField']('isSpendingNursery') ===
                'no',
                            'nurseryCost'
                        );

                        if (sheetJson?.jobless?.members) {
                            sheetJson.jobless.members = sheetJson?.jobless?.members?.filter(
                                (member) =>
                                    sheetJson?.jobless?.selectedMembers?.includes(member?.id) &&
                  joblessMemberSchema.isValidSync(member)
                            );
                        }
                        if (sheetJson?.cashReceipts?.members) {
                            sheetJson.cashReceipts.members = sheetJson?.cashReceipts?.members?.filter(
                                (member) =>
                                    sheetJson?.cashReceipts?.activeMembers?.includes(member?.id) &&
                  cashReceiptsMemberSchema.isValidSync(member)
                            );
                        }
                        if (sheetJson?.cashReceipts?.noticePeriodMembers) {
                            sheetJson.cashReceipts.noticePeriodMembers =
                sheetJson?.cashReceipts?.noticePeriodMembers?.filter((member) =>
                    noticePeriodMemberSchema.isValidSync(member)
                );
                        }
                        if (sheetJson?.cashReceipts?.chosenSourceDetails) {
                            sheetJson.cashReceipts.chosenSourceDetails =
                sheetJson?.cashReceipts?.chosenSourceDetails?.filter((member) =>
                    chosenSourceDetailsSchema.isValidSync(member)
                );
                        }
                        if (sheetJson.expensesForMedicine.members) {
                            sheetJson.expensesForMedicine.members =
                sheetJson?.expensesForMedicine?.members?.filter((member) =>
                    sheetJson?.expensesForMedicine?.selectedMembers?.includes(member?.id)
                );
                            sheetJson.expensesForMedicine.members?.forEach((el) => {
                                el.monthlyCostOfTreatment = this.formatDecimalVariable(el.monthlyCostOfTreatment);
                            });
                        }

                        if (sheetJson.expensesForMedicine.illMembers) {
                            sheetJson.expensesForMedicine.illMembers =
                sheetJson?.expensesForMedicine?.illMembers?.filter(
                    (member) =>
                        sheetJson?.expensesForMedicine?.selectedIllMembers?.includes(member?.id) &&
                    illMemberSchema.isValidSync(member)
                );
                            sheetJson.expensesForMedicine.illMembers?.forEach((el) => {
                                el.monthlyCostOfTreatment = this.formatDecimalVariable(el.monthlyCostOfTreatment);
                            });
                        }

                        if (sheetJson.expensesForMedicine.disabledMembers) {
                            sheetJson.expensesForMedicine.disabledMembers =
                sheetJson?.expensesForMedicine?.disabledMembers?.filter(
                    (member) =>
                        sheetJson?.expensesForMedicine?.selectedDisabledMembers?.includes(member?.id) &&
                    disabledMemberSchema.isValidSync(member)
                );
                            sheetJson.expensesForMedicine.disabledMembers?.forEach((el) => {
                                el.monthlyCostOfTreatment = this.formatDecimalVariable(el.monthlyCostOfTreatment);
                            });
                        }

                        if (!sheetJson.expensesForMedicine.selectedDisabledMembers?.length) {
                            delete sheetJson.expensesForMedicine.disabledMembers;
                        }
                        sheetJson.expensesForMedicine?.disabledMembers?.forEach((member, index) => {
                            delete member.complete;
                            delete member.name;
                            if (!sheetJson.expensesForMedicine.selectedDisabledMembers?.includes(member?.id)) {
                                sheetJson.expensesForMedicine.disabledMembers[index] = null;
                            }
                        });

                        if (!sheetJson.expensesForMedicine.selectedIllMembers?.length) {
                            delete sheetJson.expensesForMedicine.illMembers;
                        }
                        sheetJson.expensesForMedicine?.illMembers?.forEach((member, index) => {
                            delete member.complete;
                            delete member.name;
                            if (!sheetJson.expensesForMedicine.selectedIllMembers?.includes(member?.id)) {
                                sheetJson.expensesForMedicine.illMembers[index] = null;
                            }
                        });

                        if (!sheetJson.cashReceipts.members?.length) delete sheetJson.cashReceipts.members;
                        sheetJson.cashReceipts?.members?.forEach((member, index) => {
                            if (!sheetJson.cashReceipts.activeMembers?.includes(member?.id)) {
                                sheetJson.cashReceipts.members[index] = null;
                            }
                        });

                        if (!sheetJson.jobless.members?.length) delete sheetJson.jobless.members;
                        sheetJson.jobless?.members?.forEach((member, index) => {
                            if (!sheetJson.jobless.selectedMembers?.includes(member?.id)) {
                                sheetJson.jobless.members[index] = null;
                            }
                        });

                        if (has(sheetJson?.maintenanceExpenses, 'nurseryCost')) {
                            sheetJson.maintenanceExpenses.nurseryCost = this.formatDecimalVariable(
                                sheetJson.maintenanceExpenses.nurseryCost
                            );
                        }

                        if (has(sheetJson?.maintenanceExpenses, 'details')) {
                            sheetJson.maintenanceExpenses.details.costPerMonth = this.formatDecimalVariable(
                                sheetJson.maintenanceExpenses.details.costPerMonth
                            );
                            if (has(sheetJson?.maintenanceExpenses?.details, 'costPerTon')) {
                                sheetJson.maintenanceExpenses.details.costPerTon = this.formatDecimalVariable(
                                    sheetJson.maintenanceExpenses.details.costPerTon
                                );
                            }
                            sheetJson.maintenanceExpenses.details.costPerYear = this.formatDecimalVariable(
                                sheetJson.maintenanceExpenses.details.costPerYear
                            );
                        }

                        sheetJson.maintenanceExpenses.costs.rent = this.formatDecimalVariable(
                            sheetJson.maintenanceExpenses.costs.rent
                        );
                        sheetJson.maintenanceExpenses.costs.electricity = this.formatDecimalVariable(
                            sheetJson.maintenanceExpenses.costs.electricity
                        );
                        sheetJson.maintenanceExpenses.costs.gas = this.formatDecimalVariable(
                            sheetJson.maintenanceExpenses.costs.gas
                        );
                        sheetJson.maintenanceExpenses.costs.water = this.formatDecimalVariable(
                            sheetJson.maintenanceExpenses.costs.water
                        );
                        sheetJson.maintenanceExpenses.costs.internet = this.formatDecimalVariable(
                            sheetJson.maintenanceExpenses.costs.internet
                        );
                        sheetJson.maintenanceExpenses.costs.phone = this.formatDecimalVariable(
                            sheetJson.maintenanceExpenses.costs.phone
                        );

                        if (has(sheetJson?.credits, 'creditDetails')) {
                            sheetJson.credits.creditDetails.creditAmount = this.formatDecimalVariable(
                                sheetJson.credits.creditDetails.creditAmount
                            );
                            sheetJson.credits.creditDetails.remainsToRepair = this.formatDecimalVariable(
                                sheetJson.credits.creditDetails.remainsToRepair
                            );
                            sheetJson.credits.creditDetails.monthCreditAmount = this.formatDecimalVariable(
                                sheetJson.credits.creditDetails.monthCreditAmount
                            );
                        }

                        if (has(sheetJson?.credits, 'arrearsAmount')) {
                            sheetJson.credits.arrearsAmount = this.formatDecimalVariable(
                                sheetJson.credits.arrearsAmount
                            );
                        }

                        if (has(sheetJson?.cashReceipts, 'chosenSourceDetails')) {
                            sheetJson.cashReceipts.chosenSourceDetails?.forEach((el) => {
                                if (el.amount) {
                                    el.amount = this.formatDecimalVariable(el.amount);
                                }
                            });
                        }

                        if (has(sheetJson?.cashReceipts, 'noticePeriodMembers')) {
                            sheetJson.cashReceipts.noticePeriodMembers?.forEach((el) => {
                                delete el.name;
                                if (!el.confirmation) {
                                    delete el.description;
                                }
                            });
                        }

                        if (sheetJson?.jobless?.members) {
                            sheetJson.jobless.members = sheetJson.jobless.members?.filter(Boolean);
                        }
                        if (sheetJson?.cashReceipts?.members) {
                            sheetJson.cashReceipts.members = sheetJson.cashReceipts.members?.filter(Boolean);
                        }
                        if (sheetJson?.expensesForMedicine?.illMembers) {
                            sheetJson.expensesForMedicine.illMembers =
                sheetJson.expensesForMedicine.illMembers?.filter(Boolean);
                        }
                        if (sheetJson?.expensesForMedicine?.disabledMembers) {
                            sheetJson.expensesForMedicine.disabledMembers =
                sheetJson.expensesForMedicine.disabledMembers?.filter(Boolean);
                        }

                        if (has(sheetJson?.cashReceipts, 'members')) {
                            sheetJson.cashReceipts.members?.forEach((el) => {
                                if (el?.income) {
                                    el.income = this.formatDecimalVariable(el.income);
                                }
                            });
                        }

                        sheetJson.medicsPack = sheetJson.doctorsPack;
                        delete sheetJson.doctorsPack;
                        if (this.isCurrentEdition) {
                            delete sheetJson.medicsPack;
                            delete sheetJson.lawyersPack;
                            delete sheetJson.expensesForMedicine.members;
                        }
                    }
                    if (this.sheetType === 'reflection') {
                        sheetJson = cloneDeep(this.$store.getters['sheet/getReflectionFields']);
                        fieldRemover(sheetJson);
                        fieldRemover(
                            sheetJson,
                            'seniorReflection',
                            this.$store.getters['sheet/getJoblessField']('aboutSixty') !== 'yes'
                        );
                        fieldRemover(
                            sheetJson,
                            'precludeFactors',
                            this.$store.getters['sheet/getSeniorReflectionField']('hasPrecludeFactors') !== 'yes'
                        );
                        fieldRemover(
                            sheetJson,
                            'resources',
                            this.$store.getters['sheet/getReflectionResourcesField']('hasStrengths') === 'no',
                            'strengths'
                        );

                        const influenced =
              this.$store.getters['sheet/getReflectionBarriersField']('breakCompetence').length ===
                this.$store.getters['sheet/getReflectionBarriersField']('competence').length &&
              this.$store.getters['sheet/getReflectionBarriersField']('breakBeliefs').length ===
                this.$store.getters['sheet/getReflectionBarriersField']('beliefs').length &&
              this.$store.getters['sheet/getReflectionBarriersField']('breakCondition').length ===
                this.$store.getters['sheet/getReflectionBarriersField']('condition').length &&
              this.$store.getters['sheet/getReflectionBarriersField']('breakSupport').length ===
                this.$store.getters['sheet/getReflectionBarriersField']('support').length;

                        fieldRemover(
                            sheetJson,
                            'barriers',
                            influenced === true,
                            'influencedCompetence influencedBeliefs influencedCondition influencedSupport hasInfluence'
                        );

                        fieldRemover(
                            sheetJson,
                            'barriers',
                            this.$store.getters['sheet/getReflectionBarriersField']('cantBreakBarriers') === true,
                            'influencedCompetence influencedBeliefs influencedCondition influencedSupport hasInfluence'
                        );

                        fieldRemover(
                            sheetJson,
                            'barriers',
                            this.$store.getters['sheet/getReflectionBarriersField']('hasInfluence') === 'no',
                            'influencedCompetence influencedBeliefs influencedCondition influencedSupport'
                        );

                        // Remove empty objects from sheetJson
                        Object.entries(sheetJson).forEach((entry) => {
                            const [key, value] = entry;
                            if (isEmpty(value)) delete sheetJson[key];
                        });
                    }
                    if (this.sheetType === 'decision') {
                        sheetJson = cloneDeep(this.$store.getters['sheet/getDecisionFields']);
                        fieldRemover(sheetJson);
                        fieldRemover(
                            sheetJson,
                            '',
                            this.$store.getters['sheet/getDecisionFields'].isTurnOn === 'yes',
                            'nonInclusion'
                        );
                        fieldRemover(
                            sheetJson,
                            'turnOn',
                            this.$store.getters['sheet/getPreviousEditionsField']('helpReceived') !== 'yes',
                            'whyIncludeAgain'
                        );
                        fieldRemover(
                            sheetJson,
                            '',
                            this.$store.getters['sheet/getDecisionFields'].isTurnOn === 'no',
                            'turnOn'
                        );
                        fieldRemover(
                            sheetJson,
                            'nonInclusion',
                            this.$store.getters['sheet/getDecisionFields'].nonInclusion.reason.includes(4) ===
                false,
                            'otherReason'
                        );
                    }

                    if (this.sheetType === 'needs') {
                        sheetJson = cloneDeep(this.$store.getters['sheet/getSheetTwo']);
                        fieldRemover(sheetJson);
                        fieldRemover(
                            sheetJson,
                            'firstMeeting',
                            this.$store.getters['sheet/getFirstMeetingField']('askedQuestions') !== 'yes',
                            'questionsList'
                        );
                        fieldRemover(
                            sheetJson,
                            'food',
                            this.$store.getters['sheet/getFoodFields']('chosenProducts').includes(206) === false,
                            'otherProducts'
                        );
                        fieldRemover(
                            sheetJson,
                            'food',
                            this.$store.getters['sheet/getFoodFields']('chosenProducts').includes(205) === false,
                            'productsForBabies'
                        );
                        fieldRemover(
                            sheetJson,
                            'food',
                            this.$store.getters['sheet/getFoodFields']('hasDietaryRequirements') !== 'yes',
                            'dietaryRequirements'
                        );
                        fieldRemover(
                            sheetJson,
                            'cleaningProducts',
                            this.$store.getters['sheet/getCleaningProductsFields']('chosenProducts').includes(
                                215
                            ) === false,
                            'childrenProducts'
                        );
                        fieldRemover(
                            sheetJson,
                            'cleaningProducts',
                            this.$store.getters['sheet/getCleaningProductsFields']('chosenProducts').includes(
                                216
                            ) === false,
                            'otherProducts'
                        );
                        fieldRemover(
                            sheetJson,
                            'cleaningProducts',
                            this.$store.getters['sheet/getCleaningProductsFields']('hasSpecialRequirements') !==
                'yes',
                            'specialRequirements'
                        );
                        fieldRemover(
                            sheetJson,
                            'schoolSupplies',
                            this.$store.getters['sheet/getSchoolSuppliesFields']('needSchoolSupplies') !== 'yes',
                            'selectedSupplies'
                        );
                        fieldRemover(
                            sheetJson,
                            'schoolSupplies',
                            this.$store.getters['sheet/getSchoolSuppliesFields']('needSchoolSupplies') !== 'yes',
                            'anotherSchoolSupplies'
                        );
                        fieldRemover(
                            sheetJson,
                            'schoolSupplies',
                            this.$store.getters['sheet/getSchoolSuppliesFields']('selectedSupplies').includes(
                                370
                            ) === false,
                            'anotherSchoolSupplies'
                        );
                        fieldRemover(
                            sheetJson,
                            'apartmentEquipment',
                            !has(sheetJson?.apartmentEquipment, 'selectedEquipment')
                        );
                        fieldRemover(
                            sheetJson,
                            'apartmentEquipment',
                            this.$store.getters['sheet/getApartmentEquipmentFields'](
                                'selectedEquipment'
                            ).includes(278) === false,
                            'anotherEquipment'
                        );
                        fieldRemover(
                            sheetJson,
                            'householdAppliances',
                            this.$store.getters['sheet/getHouseholdAppliances']('selectedAppliances').includes(
                                293
                            ) === false,
                            'anotherAppliances'
                        );
                        fieldRemover(
                            sheetJson,
                            'otherMaterialNeeds',
                            this.$store.getters['sheet/getOtherMaterialNeedsFields']('hasOtherNeeds') !== 'yes',
                            'selectedMaterialNeeds'
                        );
                        fieldRemover(
                            sheetJson,
                            'otherMaterialNeeds',
                            this.$store.getters['sheet/getOtherMaterialNeedsFields']('hasOtherNeeds') !== 'yes',
                            'fuelType'
                        );
                        fieldRemover(
                            sheetJson,
                            'otherMaterialNeeds',
                            this.$store.getters['sheet/getOtherMaterialNeedsFields']('hasOtherNeeds') !== 'yes',
                            'fuelAmount'
                        );
                        fieldRemover(
                            sheetJson,
                            'otherMaterialNeeds',
                            this.$store.getters['sheet/getOtherMaterialNeedsFields']('hasOtherNeeds') !== 'yes',
                            'otherFuelType'
                        );
                        fieldRemover(
                            sheetJson,
                            'otherMaterialNeeds',
                            this.$store.getters['sheet/getOtherMaterialNeedsFields']('hasOtherNeeds') !== 'yes',
                            'anotherMaterialNeeds'
                        );
                        fieldRemover(
                            sheetJson,
                            'otherMaterialNeeds',
                            this.$store.getters['sheet/getOtherMaterialNeedsFields'](
                                'selectedMaterialNeeds'
                            ).includes(344) === false,
                            'fuelType'
                        );
                        fieldRemover(
                            sheetJson,
                            'otherMaterialNeeds',
                            this.$store.getters['sheet/getOtherMaterialNeedsFields'](
                                'selectedMaterialNeeds'
                            ).includes(344) === false,
                            'fuelAmount'
                        );
                        fieldRemover(
                            sheetJson,
                            'otherMaterialNeeds',
                            this.$store.getters['sheet/getOtherMaterialNeedsFields'](
                                'selectedMaterialNeeds'
                            ).includes(344) === false,
                            'otherFuelType'
                        );
                        fieldRemover(
                            sheetJson,
                            'otherMaterialNeeds',
                            this.$store.getters['sheet/getOtherMaterialNeedsFields'](
                                'selectedMaterialNeeds'
                            ).includes(345) === false,
                            'anotherMaterialNeeds'
                        );
                        fieldRemover(
                            sheetJson,
                            'intangibleNeeds',
                            this.$store.getters['sheet/getIntangibleNeedsFields']('hasIntangibleNeeds') !== 'yes',
                            'intangibleNeedsList'
                        );
                        fieldRemover(
                            sheetJson,
                            'clothing',
                            this.$store.getters['sheet/getClothingFields']('needClothes') !== 'yes',
                            'chosenClothesDetails'
                        );
                        fieldRemover(
                            sheetJson,
                            'clothing',
                            this.$store.getters['sheet/getClothingFields']('needClothes') !== 'yes',
                            'chosenClothesMembers'
                        );
                        fieldRemover(
                            sheetJson,
                            'clothing',
                            this.$store.getters['sheet/getClothingFields']('needFootwear') !== 'yes',
                            'chosenFootwearMembers'
                        );
                        fieldRemover(
                            sheetJson,
                            'clothing',
                            this.$store.getters['sheet/getClothingFields']('needFootwear') !== 'yes',
                            'chosenFootwearDetails'
                        );
                        fieldRemover(sheetJson, 'consents', !has(sheetJson?.consents, 'date'));
                        sheetJson?.changedNames.newNames?.forEach((el) => delete el.name);
                        sheetJson?.importantNeeds?.list?.forEach((need) => {
                            if (need.noteNotNeeded) delete need.note;
                            else delete need.noteNotNeeded;
                        });
                        if (Array.isArray(sheetJson?.specialGifts?.members)) {
                            sheetJson.specialGifts.members = sheetJson.specialGifts.members?.filter(member => !!member.gift);
                        }
                    }

                    if (this.sheetType === 'description') {
                        sheetJson = cloneDeep(this.$store.getters['description/getDescription']);
                        fieldRemover(sheetJson);
                    }

                    if (this.publicationRevokedToNational || this.canDoAuditOnNational) {
                        this.sendSheetCorrectionsNational(sheetJson)
                            .then(() => {
                                this.clearValidatorFlags();
                                this.goToNextSection();
                            })
                            .finally(() => {
                                this.setButtonIndicatorProps(false);
                            });
                    } else if (this.canDoAuditInArea) {
                        this.sendSheetCorrections(sheetJson)
                            .then(() => {
                                this.clearValidatorFlags();
                                this.goToNextSection();
                            })
                            .finally(() => {
                                this.setButtonIndicatorProps(false);
                            });
                    } else if (this.isSheetReturned) {
                        this.sendSheetCorrectionsVolunteer(sheetJson)
                            .then(() => {
                                this.clearValidatorFlags();
                                this.goToNextSection();
                            })
                            .finally(() => {
                                this.setButtonIndicatorProps(false);
                            });
                    } else {
                        this.$store
                            .dispatch('sheet/saveSheet', {
                                familyId: this.familyId,
                                sheetType: this.sheetType,
                                data: sheetJson
                            })
                            .then(() => {
                                this.clearValidatorFlags();
                                this.goToNextSection();
                            })
                            .finally(() => {
                                this.setButtonIndicatorProps(false);
                            });
                    }
                } else {
                    this.setButtonIndicatorProps(false);
                    this.scrollToTopErrorMessage();
                }
            });
        },
        clearValidatorFlags () {
            Object.keys(this.$validator.flags)?.forEach((x) => {
                this.$validator.flags[x].changed = false;
            });
        }
    }
};
</script>

<style scoped></style>
